import L from "leaflet";
// import 'leaflet.motion/dist/leaflet.motion.js'; // Commented out - library not available
import { fetchIndustryData, fetchLogisticLocations, fetchRegionBoundary } from "./data.js";
import { handleMarkerClick, initNavigation } from "./navigation.js";

let map;
let initialBounds;
let boundaryLayer;
const boundaryColor = [
  "#e6194B",
  "#3cb44b",
  "#ffe119",
  "#4363d8",
  "#f58231",
  "#911eb4",
  "#46f0f0",
  "#f032e6",
  "#bcf60c",
  "#fabebe",
  "#008080",
  "#e6beff",
  "#9A6324",
  "#fffac8",
  "#800000",
  "#aaffc3",
  "#808000",
  "#ffd8b1",
  "#000075",
  "#a9a9a9",
  "#ffffff",
  "#000000",
];

/**
 * Initializes the Leaflet map.
 * @param {string} mapId The ID of the map container element.
 * @returns {L.Map} The Leaflet map instance.
 */
export function initMap(mapId) {
  map = L.map(mapId);
  L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  }).addTo(map);

  map.whenReady(() => {
    initialBounds = map.getBounds();
    // Dispatch a custom event to signal that the map is ready
    const event = new CustomEvent("mapReady");
    document.getElementById(mapId).dispatchEvent(event);
  });

  // Initialize navigation functionality
  initNavigation(map);

  return map;
}

/**
 * Resets the map to its initial view.
 */
export function resetMap() {
  if (boundaryLayer) {
    boundaryLayer.setStyle({ fillOpacity: 0.1 });
  }
  if (initialBounds) {
    map.flyToBounds(initialBounds, { animate: true, duration: 1.5 });
  }
}

/**
 * Fetches and displays the main industry marker on the map.
 * @param {string} locationId The ID of the location.
 */
export async function displayIndustryMarker(locationId) {
  const loadingIndicator = document.getElementById("loading-indicator");
  loadingIndicator.style.display = "flex";

  try {
    const data = await fetchIndustryData(locationId);
    const bounds = L.latLngBounds([]);

    if (data.acf && data.acf.industry_lat && data.acf.industry_long) {
      const lat = parseFloat(data.acf.industry_lat);
      const long = parseFloat(data.acf.industry_long);
      const customIcon = L.icon({
        iconUrl: "/wp-content/themes/industry/img/pin.png",
        iconSize: [40, 51],
        iconAnchor: [20, 51],
      });
      const marker = L.marker([lat, long], {
        icon: customIcon,
        riseOnHover: true,
      });
      marker.addTo(map);
      bounds.extend([lat, long]);
    }
    if (bounds.isValid()) {
      map.fitBounds(bounds, { maxZoom: 15 });
    }
  } catch (error) {
    console.error("Error fetching industry data:", error);
  } finally {
    loadingIndicator.style.display = "none";
  }
}

/**
 * Fetches and displays logistic location markers on the map.
 */
export async function displayLogisticMarkers() {
  try {
    const data = await fetchLogisticLocations();
    data.forEach((logistic) => {
      let basePath = "/wp-content/themes/iphub/img/";
      let iconUrl = basePath + "default_marker.png";

      if (logistic.mps_logistic_cat.includes(110) || logistic.mps_logistic_cat.includes(117)) {
        iconUrl = basePath + "anchor.svg";
      } else if (logistic.mps_logistic_cat.includes(118)) {
        iconUrl = basePath + "gate.svg";
      } else if (logistic.mps_logistic_cat.includes(119)) {
        iconUrl = basePath + "airport.svg";
      } else if (logistic.mps_logistic_cat.includes(120)) {
        iconUrl = basePath + "iland.svg";
      }

      const icon = L.icon({
        iconUrl: iconUrl,
        iconSize: [24, 24],
        iconAnchor: [12, 24],
        popupAnchor: [0, -24],
      });

      if (logistic.acf.lat && logistic.acf.long) {
        const marker = L.marker([logistic.acf.lat, logistic.acf.long], {
          icon: icon,
        }).addTo(map);
        marker.bindTooltip(logistic.title.rendered, {
          permanent: false,
          direction: "top",
          offset: L.point(0, -30),
        });

        // Add click event for navigation
        marker.on("click", function () {
          const markerLatLng = marker.getLatLng();
          const tooltip = marker.getTooltip();
          const tooltipContent = tooltip && tooltip.getContent ? tooltip.getContent() : null;
          handleMarkerClick(marker, markerLatLng, tooltipContent);
        });
      }
    });
  } catch (error) {
    console.error("Error fetching logistic locations:", error);
  }
}

/**
 * Fetches and displays region boundaries on the map.
 * @param {string|number} regions A comma-separated string or a single number of OSM IDs.
 */
export async function displayRegionBoundaries(regions) {
  if (!regions) return;

  let osmIds = [];
  if (typeof regions === "string" && regions.includes(",")) {
    osmIds = regions.split(",");
  } else {
    osmIds = [regions];
  }

  osmIds.forEach(async (osmId, index) => {
    try {
      const geojsonData = await fetchRegionBoundary(osmId.trim());
      boundaryLayer = L.geoJSON(geojsonData, {
        style: {
          color: boundaryColor[index % boundaryColor.length],
          weight: 2,
          fillOpacity: 0.3,
        },
      }).addTo(map);
    } catch (error) {
      console.error(`Error displaying boundary for OSM ID ${osmId}:`, error);
    }
  });
}
